import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Smartphone, Bookmark } from "lucide-react";
import { BRAND_LOGO } from "../lib/assets";

/**
 * Permanent landing page for the QR code printed on physical People Watching
 * packaging. URL must remain https://playpeoplewatching.com/app forever — only
 * the content inside changes once the native app ships (replace the "coming
 * soon" block with the App Store + Google Play buttons; keep the route).
 */
const AppLanding = () => {
  useEffect(() => {
    document.title = "People Watching App — Coming Soon";
  }, []);

  return (
    <main
      data-testid="app-landing-page"
      className="relative navy-bg grain min-h-screen flex items-center overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28"
    >
      {/* Playful confetti dots echoing the hero */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        {[
          { l: "6%",  t: "18%", c: "var(--pw-yellow)" },
          { l: "15%", t: "72%", c: "var(--pw-pink)"   },
          { l: "88%", t: "22%", c: "var(--pw-orange)" },
          { l: "82%", t: "78%", c: "var(--pw-green)"  },
          { l: "48%", t: "10%", c: "var(--pw-purple)" },
          { l: "52%", t: "88%", c: "var(--pw-blue)"   },
        ].map((d, i) => (
          <span
            key={i}
            className="absolute w-2.5 h-2.5 md:w-3 md:h-3 rounded-full"
            style={{ left: d.l, top: d.t, backgroundColor: d.c }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-2xl mx-auto px-5 md:px-8 text-center text-white">
        <motion.img
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          src={BRAND_LOGO}
          alt="People Watching"
          data-testid="app-landing-logo"
          className="h-24 md:h-32 w-auto rounded-full mx-auto mb-8 md:mb-10"
          draggable="false"
        />

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="eyebrow mb-5"
          style={{ color: "var(--pw-yellow)" }}
        >
          The Digital Experience
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="app-landing-heading"
          className="font-display font-black leading-[0.95] tracking-tighter"
          style={{ fontSize: "clamp(2.5rem, 8vw, 5.5rem)" }}
        >
          People Watching <span style={{ color: "var(--pw-yellow)" }}>App.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="app-landing-main"
          className="mt-6 md:mt-7 text-lg md:text-xl text-white/85 leading-relaxed max-w-xl mx-auto"
        >
          Your physical People Watching game includes access to the digital experience.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.42 }}
          data-testid="app-landing-coming-soon"
          className="mt-8 md:mt-10 inline-flex items-center gap-3 rounded-full px-5 py-3 md:px-6 md:py-3.5 border"
          style={{
            backgroundColor: "rgba(245,197,66,0.1)",
            borderColor: "rgba(245,197,66,0.35)",
          }}
        >
          <Smartphone className="w-5 h-5" style={{ color: "var(--pw-yellow)" }} />
          <span className="font-display font-bold text-base md:text-lg tracking-tight">
            The People Watching app is coming soon.
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          data-testid="app-landing-bookmark"
          className="mt-7 md:mt-8 inline-flex items-start gap-2 text-sm md:text-base text-white/70 max-w-md mx-auto"
        >
          <Bookmark className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--pw-pink)" }} />
          <span>
            Keep this page bookmarked — the download links will appear here when the app
            launches.
          </span>
        </motion.p>
      </div>
    </main>
  );
};

export default AppLanding;
